import React, { useEffect, useState } from "react";
import { FiCheckCircle, FiCopy, FiDollarSign, FiGift, FiUsers } from "react-icons/fi";
import { api } from "../../config/api";
import showToast from "../../utils/toastNotifications";
import { currencyFormatter } from "../../utils/formatting";

const Stat = ({ title, value, icon }) => (
  <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-soft-blue text-primary">
      {icon}
    </div>
    <p className="font-inter text-sm text-gray">{title}</p>
    <p className="mt-1 break-words font-roboto text-2xl font-bold text-primary">{value}</p>
  </div>
);

const ReferralDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [form, setForm] = useState({ paypalEmail: "", paypalFullName: "", amount: "" });

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get("/referral/me");
      setDashboard(response.data.data);
    } catch (error) {
      showToast("error", error.message || "Unable to fetch referral dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const copyReferralLink = async () => {
    await navigator.clipboard.writeText(dashboard?.referralLink || "");
    showToast("success", "Referral link copied");
  };

  const requestPayout = async (event) => {
    event.preventDefault();
    if (requesting) return;
    setRequesting(true);

    try {
      await api.post("/referral/payouts", {
        ...form,
        amount: form.amount ? Number(form.amount) : undefined,
      });
      showToast("success", "PayPal withdrawal request sent");
      setForm({ paypalEmail: "", paypalFullName: "", amount: "" });
      fetchDashboard();
    } catch (error) {
      showToast("error", error.message || "Unable to request payout");
    } finally {
      setRequesting(false);
    }
  };

  const stats = dashboard?.stats || {};
  const payout = dashboard?.payout || {};
  const program = dashboard?.program || {};
  const availableReferralEarnings = Number(stats.availableReferralCredit || 0);
  const minimumPayoutAmount = Number(payout.minimumAmount || 50);
  const amountNeededForPayout = Math.max(0, minimumPayoutAmount - availableReferralEarnings);

  if (loading) {
    return <div className="px-4 pb-6 pt-28 font-inter text-gray xl:pt-6">Loading referrals...</div>;
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] overflow-x-hidden px-4 pb-10 pt-28 sm:px-6 lg:px-8 xl:pt-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-roboto text-2xl font-bold text-primary">Referral Earnings</h1>
          <p className="mt-1 font-inter text-sm text-gray">
            Share your link and earn {currencyFormatter.format(program.REFERRER_REWARD_AMOUNT || 40)} in referral earnings after each qualifying completed order. Use your earnings toward an order or request a PayPal withdrawal once you reach the {currencyFormatter.format(minimumPayoutAmount)} minimum.
          </p>
        </div>
        <button
          onClick={copyReferralLink}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 font-inter text-sm font-semibold text-white sm:w-auto"
        >
          <FiCopy /> Copy link
        </button>
      </div>

      <div className="mb-6 min-w-0 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <div>
          <p className="font-inter text-sm font-semibold text-primary">Your referral link</p>
          <p className="mt-1 font-inter text-sm text-gray">
            Share this link anywhere students may need help. New clients who sign up through it get 50% off their first order.
          </p>
          <p className="mt-1 font-inter text-sm font-semibold text-primary">
            For each referred client who signs up with your link and gets their first order completed, you earn $40.
          </p>
        </div>
        <div className="mt-3 flex min-w-0 items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
          <div className="min-w-0 flex-1 break-all font-inter text-sm text-gray">
            {dashboard?.referralLink}
          </div>
          <button
            onClick={copyReferralLink}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 font-inter text-sm font-semibold text-primary"
          >
            <FiCopy /> Copy
          </button>
        </div>
        <div className="mt-3 rounded-lg bg-soft-blue px-3 py-2 font-inter text-sm text-primary">
          <p className="font-semibold">Your referral earnings are yours to use or withdraw.</p>
          <p className="mt-1 leading-5">
            Approved earnings appear in your available balance, but you do not have to spend them on an order. Once your unused referral earnings reach {currencyFormatter.format(minimumPayoutAmount)}, you can request a withdrawal to your PayPal account.
          </p>
          <a href="/terms-and-conditions#referral-program-terms" target="_blank" rel="noopener noreferrer" className="mt-1 inline-block font-semibold underline">
            Terms and Conditions Apply
          </a>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat title="Link clicks" value={stats.clickCount || 0} icon={<FiUsers />} />
        <Stat title="Signups" value={stats.signups || 0} icon={<FiGift />} />
        <Stat title="Approved rewards" value={stats.approved || 0} icon={<FiCheckCircle />} />
        <Stat title="Available referral earnings" value={currencyFormatter.format(availableReferralEarnings)} icon={<FiDollarSign />} />
      </div>

      <div className="mt-6 grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <h2 className="font-roboto text-lg font-bold text-primary">Referral activity</h2>
          <div className="mt-4 w-full max-w-full overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[560px] text-left font-inter text-sm sm:min-w-[620px]">
              <thead className="border-b border-slate-200 text-gray">
                <tr>
                  <th className="py-3">Date</th>
                  <th className="py-3">Qualifying order</th>
                  <th className="py-3">Status</th>
                  <th className="py-3 text-right">Reward</th>
                </tr>
              </thead>
              <tbody>
                {(dashboard?.referrals || []).length <= 0 ? (
                  <tr>
                    <td className="py-8 text-center text-gray" colSpan="4">
                      No referral activity yet.
                    </td>
                  </tr>
                ) : (
                  dashboard.referrals.map((referral) => (
                    <tr key={referral.id} className="border-b border-slate-100">
                      <td className="py-3">{new Date(referral.createdAt).toLocaleDateString()}</td>
                      <td className="py-3">{referral.hasQualifyingOrder ? "Received" : "Waiting"}</td>
                      <td className="py-3">{referral.status}</td>
                      <td className="py-3 text-right">{currencyFormatter.format(referral.rewardAmount || 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <h2 className="font-roboto text-lg font-bold text-primary">Withdraw Referral Earnings</h2>
          <p className="mt-1 font-inter text-sm leading-5 text-gray">
            Request a withdrawal of your available referral earnings through PayPal. We will ask for your PayPal email address and the full name associated with the account.
          </p>
          {!payout.eligible ? (
            <p className="mt-3 rounded-lg bg-slate-50 p-3 font-inter text-sm text-gray">
              You currently have {currencyFormatter.format(availableReferralEarnings)} in available referral earnings. PayPal withdrawals become available at {currencyFormatter.format(minimumPayoutAmount)}. You need {currencyFormatter.format(amountNeededForPayout)} more to request a withdrawal.
            </p>
          ) : (
            <form onSubmit={requestPayout} className="mt-4 space-y-3">
              <p className="rounded-lg bg-slate-50 p-3 font-inter text-sm leading-5 text-gray">
                You can request between {currencyFormatter.format(minimumPayoutAmount)} and {currencyFormatter.format(payout.availableAmount || 0)}. Requests are reviewed and processed manually through PayPal.
              </p>
              <label htmlFor="referral-paypal-email" className="block font-inter text-sm font-semibold text-primary">PayPal email address</label>
              <input
                id="referral-paypal-email"
                type="email"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-inter text-sm"
                placeholder="PayPal email address"
                value={form.paypalEmail}
                onChange={(e) => setForm({ ...form, paypalEmail: e.target.value })}
                required
              />
              <label htmlFor="referral-paypal-name" className="block font-inter text-sm font-semibold text-primary">Full name on PayPal account</label>
              <input
                id="referral-paypal-name"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-inter text-sm"
                placeholder="Full name on PayPal"
                value={form.paypalFullName}
                onChange={(e) => setForm({ ...form, paypalFullName: e.target.value })}
                required
              />
              <label htmlFor="referral-withdrawal-amount" className="block font-inter text-sm font-semibold text-primary">Withdrawal amount</label>
              <input
                id="referral-withdrawal-amount"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-inter text-sm"
                placeholder={`Amount up to ${currencyFormatter.format(payout.availableAmount || 0)}`}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                type="number"
                min={payout.minimumAmount || 50}
                max={payout.availableAmount || 0}
              />
              <p className="font-inter text-xs leading-5 text-gray">
                Please verify your PayPal details carefully. Incorrect account information may delay your payment.
              </p>
              <button className="w-full rounded-lg bg-primary px-4 py-2 font-inter text-sm font-semibold text-white" disabled={requesting}>
                {requesting ? "Sending..." : "Request PayPal Withdrawal"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralDashboard;
